import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const features = [
        {
            icon: '🤝',
            title: 'Community Support',
            description: 'Join a network of caring individuals who support each other during life\'s most challenging moments.'
        },
        {
            icon: '💝',
            title: 'Mutual Assistance',
            description: 'Give and receive financial support when members experience the loss of a loved one.'
        },
        {
            icon: '🛡️',
            title: 'Financial Security',
            description: 'Peace of mind knowing your family is protected by a community that cares.'
        },
        {
            icon: '📱',
            title: 'Easy Contributions',
            description: 'Simple, transparent contribution system with voucher-based payments and wallet options.'
        },
        {
            icon: '🔒',
            title: 'Secure & Verified',
            description: 'All members and beneficiaries are properly verified with document uploads for security.'
        },
        {
            icon: '🌍',
            title: 'Trusted Network',
            description: 'Build meaningful connections within a trusted community of supportive individuals.'
        }
    ];

    const howItWorks = [
        {
            step: 1,
            title: 'Join Community',
            description: 'Sign up and complete verification with ID and beneficiary setup',
            icon: '👥'
        },
        {
            step: 2,
            title: 'Get Verified',
            description: 'Upload required documents and set up your beneficiary relationships',
            icon: '✅'
        },
        {
            step: 3,
            title: 'Support Others',
            description: 'Contribute when community members experience loss',
            icon: '💝'
        },
        {
            step: 4,
            title: 'Receive Support',
            description: 'Get community support when you lose a loved one',
            icon: '🛡️'
        }
    ];

    const testimonials = [
        {
            name: 'Sarah Johnson',
            role: 'Chema Member',
            content: 'When I lost my husband, the Chema community was there for me. The support helped me get through the most difficult time of my life.',
            avatar: 'SJ'
        },
        {
            name: 'Mike Chen',
            role: 'Chema Member',
            content: 'Knowing that my family is protected gives me peace of mind. The community is genuine and caring.',
            avatar: 'MC'
        },
        {
            name: 'Maria Rodriguez',
            role: 'Chema Member',
            content: 'The verification process made me feel secure. I know everyone in the community is properly vetted.',
            avatar: 'MR'
        }
    ];

    const stats = [
        { number: '10,000+', label: 'Community Members' },
        { number: '$2M+', label: 'Total Support Given' },
        { number: '500+', label: 'Families Helped' },
        { number: '99%', label: 'Member Satisfaction' }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="bg-white shadow-sm border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                C
                            </div>
                            <span className="ml-3 text-xl font-bold text-gray-900">Chema</span>
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#features" className="text-gray-700 hover:text-primary-600 transition-colors">Features</a>
                            <a href="#how-it-works" className="text-gray-700 hover:text-primary-600 transition-colors">How It Works</a>
                            <a href="#testimonials" className="text-gray-700 hover:text-primary-600 transition-colors">Testimonials</a>
                            <Link to="/login" className="text-gray-700 hover:text-primary-600 transition-colors">Sign In</Link>
                            <Link
                                to="/sign-up"
                                className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors font-medium"
                            >
                                Join Now
                            </Link>
                        </div>

                        {/* Mobile menu button */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="text-gray-700 hover:text-primary-600 transition-colors"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    {isMenuOpen && (
                        <div className="md:hidden py-4 border-t border-gray-200">
                            <div className="flex flex-col space-y-4">
                                <a href="#features" className="text-gray-700 hover:text-primary-600 transition-colors">Features</a>
                                <a href="#how-it-works" className="text-gray-700 hover:text-primary-600 transition-colors">How It Works</a>
                                <a href="#testimonials" className="text-gray-700 hover:text-primary-600 transition-colors">Testimonials</a>
                                <Link to="/login" className="text-gray-700 hover:text-primary-600 transition-colors">Sign In</Link>
                                <Link
                                    to="/register"
                                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors font-medium text-center"
                                >
                                    Join Now
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
                <div className="absolute inset-0 bg-white/50"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Hero Content */}
                        <div className="text-center lg:text-left">
                            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                                Support When It
                                <span className="text-primary-600 block">Matters Most</span>
                            </h1>
                            <p className="mt-6 text-xl text-gray-600 max-w-2xl">
                                Join Chema - a compassionate community that provides financial support during life's most difficult moments.
                                Together, we help each other through times of loss.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <Link
                                    to="/register"
                                    className="bg-primary-500 text-white px-8 py-4 rounded-lg hover:bg-primary-600 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg"
                                >
                                    Join Our Community
                                </Link>
                                <a
                                    href="#how-it-works"
                                    className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg hover:border-primary-500 hover:text-primary-600 transition-all font-semibold text-lg"
                                >
                                    Learn More
                                </a>
                            </div>

                            {/* Trust Indicators */}
                            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-500">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                        <span className="text-green-600 text-lg">✓</span>
                                    </div>
                                    <span>Secure & Verified Members</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                        <span className="text-green-600 text-lg">✓</span>
                                    </div>
                                    <span>Instant Support</span>
                                </div>
                            </div>
                        </div>

                        {/* Hero Visual */}
                        <div className="relative">
                            <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-2 hover:rotate-0 transition-transform duration-300">
                                <div className="bg-gradient-to-br from-primary-50 to-blue-100 rounded-xl p-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Community Stats */}
                                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                            <div className="text-2xl font-bold text-primary-600">10K+</div>
                                            <div className="text-sm text-gray-600">Members</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                            <div className="text-2xl font-bold text-green-600">$2M+</div>
                                            <div className="text-sm text-gray-600">Given</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                            <div className="text-2xl font-bold text-blue-600">500+</div>
                                            <div className="text-sm text-gray-600">Families</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                            <div className="text-2xl font-bold text-purple-600">99%</div>
                                            <div className="text-sm text-gray-600">Satisfied</div>
                                        </div>
                                    </div>

                                    {/* Active Support Case */}
                                    <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-semibold text-yellow-800">Active Support Case</div>
                                                <div className="text-sm text-yellow-600">Sarah J. needs community support</div>
                                            </div>
                                            <div className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm">
                                                847/1000
                                            </div>
                                        </div>
                                        <div className="mt-2 w-full bg-yellow-200 rounded-full h-2">
                                            <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '84.7%' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating Elements */}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-3xl">🤝</span>
                            </div>
                            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-2xl">💝</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Wave Divider */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" className="fill-white"></path>
                        <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" className="fill-white"></path>
                        <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" className="fill-white"></path>
                    </svg>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl md:text-4xl font-bold text-primary-600">{stat.number}</div>
                                <div className="text-gray-600 mt-2">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Why Choose Chema?
                        </h2>
                        <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                            We've built a platform that puts community, security, and compassion first
                        </p>
                    </div>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                            >
                                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center text-white text-2xl mb-6">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section id="how-it-works" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            How Chema Works
                        </h2>
                        <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                            Simple, transparent, and built on trust
                        </p>
                    </div>

                    <div className="mt-16 relative">
                        {/* Timeline */}
                        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2"></div>

                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                            {howItWorks.map((step, index) => (
                                <div key={index} className="relative text-center">
                                    {/* Step Number */}
                                    <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-6 relative z-10 shadow-lg">
                                        {step.step}
                                    </div>

                                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                                        <div className="text-3xl mb-4">{step.icon}</div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                            {step.title}
                                        </h3>
                                        <p className="text-gray-600 text-sm">
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-16 text-center">
                        <Link
                            to="/register"
                            className="bg-primary-500 text-white px-8 py-4 rounded-lg hover:bg-primary-600 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg inline-block"
                        >
                            Start Your Journey
                        </Link>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Stories from Our Community
                        </h2>
                        <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                            Hear from members who have experienced the power of community support
                        </p>
                    </div>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold">
                                        {testimonial.avatar}
                                    </div>
                                    <div className="ml-4">
                                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                                        <div className="text-primary-600 text-sm">{testimonial.role}</div>
                                    </div>
                                </div>
                                <p className="text-gray-600 italic leading-relaxed">
                                    "{testimonial.content}"
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-20 bg-gradient-to-r from-primary-500 to-primary-600">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Ready to Join Our Caring Community?
                    </h2>
                    <p className="text-xl text-primary-100 mb-10 max-w-2xl mx-auto">
                        Become part of a network that supports each other through life's challenges.
                        Your journey to financial security and community support starts here.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-white text-primary-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg"
                        >
                            Join Chema Today
                        </Link>
                        <Link
                            to="/login"
                            className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-primary-600 transition-all font-semibold text-lg"
                        >
                            Sign In
                        </Link>
                    </div>

                    {/* Trust Badges */}
                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-12 text-white text-sm">
                        <div className="flex items-center">
                            <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-2">
                                <span className="text-white text-xs">✓</span>
                            </div>
                            <span>Bank-level Security</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-2">
                                <span className="text-white text-xs">✓</span>
                            </div>
                            <span>24/7 Support</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-2">
                                <span className="text-white text-xs">✓</span>
                            </div>
                            <span>Verified Members</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {/* Brand */}
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                    C
                                </div>
                                <span className="ml-3 text-xl font-bold">Chema</span>
                            </div>
                            <p className="mt-4 text-gray-400 max-w-md">
                                A compassionate community providing mutual financial support during life's most challenging moments.
                                Together, we're stronger.
                            </p>
                        </div>

                        {/* Links */}
                        <div>
                            <h3 className="font-semibold mb-4">Platform</h3>
                            <ul className="space-y-2 text-gray-400">
                                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                                <li><Link to="/register" className="hover:text-white transition-colors">Join Now</Link></li>
                                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-4">Support</h3>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400">
                        <p>&copy; 2024 Chema Community. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;